// src/hooks/useBaselineActions.js
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { trackPromise } from "react-promise-tracker";
import { useTracking } from "react-tracking";

import {
	updateBaseline,
	createBaseline,
	deleteBaseline,
	copyBaseline,
	importBaseline,
	updateStatus,
	updateItemThumbnails,
	dumpOneBaseline,
} from "../slices/baselineSlice";

import { useDialogBox } from "../hooks/useDialogBox";
import { useBaselinesData } from "../hooks/useBaselinesData";

import { exportJSON, exportSpreadsheet } from "../utils/fileUtils";
import { Session } from "../utils/sessionUtils";
import { debugLog, errorLog } from "../utils/debugUtils";

/**
 * All baseline CRUD and confirmation flows.
 * Accepts callbacks and state setters from the main component.
 *
 * Action Type					Level		Example Log
 * Action called/entry	2				Entry point for any user-initiated CRUD (e.g., createBaselineAction)
 * Major CRUD result		3				Success of a CRUD operation (e.g., Baseline created/updated/deleted/copied/imported/exported)
 * Calculation/object		4				Data structures, transformations, object/array state, intermediates
 * Deep debugging				9				(Reserved for firehose/full state dumps or render counts; not used below)
 */
export function useBaselineActions({
	fetchBaselineByIdOrUuid,
	fetchBaselines,
	clearDataError,
	handleBackToList,
	localBaseline,
	activeStep,
	selectedBaseline,
	lookups,
	setLocalBaseline,
}) {
	const dispatch = useDispatch();
	const { trackEvent } = useTracking();
	const { showDialog, withError } = useDialogBox();
	const { fetchBaselineForExport } = useBaselinesData();

	// CREATE
	const createBaselineAction = useCallback(() => {
		debugLog(3, "[useBaselineActions][ACTION] createBaselineAction", {
			localBaseline: localBaseline,
		});
		debugLog(2, "[useBaselineActions][FLOW] createBaseline started", {});

		trackPromise(
			dispatch(createBaseline({ data: localBaseline }))
				.unwrap()
				.then((response) => {
					showDialog(
						{
							title: "Successful",
							message: ["The Baseline was created successfully!"],
						},
						() => {
							fetchBaselineByIdOrUuid && fetchBaselineByIdOrUuid(response.id);
							clearDataError();
						}
					);

					trackEvent({ event: "createBaseline", props: { to: localBaseline } });
				})
				.catch((error) => {
					errorLog(2, "[useBaselineActions][FLOW] createBaseline failed", { error: error });
					console.error("createBaseline failed", error);
					// Create is a mutation, so keep the shared message contract but
					// intentionally skip retry to avoid duplicate writes on response loss.
					withError(error, null, {
						fallbackMessage: "Baseline create failed.",
					});
				})
		);
	}, [
		dispatch,
		localBaseline,
		fetchBaselineByIdOrUuid,
		clearDataError,
		showDialog,
		trackEvent,
		withError,
	]);

	// UPDATE
	const updateBaselineAction = useCallback(
		(step) => {
			debugLog(3, "[useBaselineActions][ACTION] updateBaselineAction", {
				step: step,
				localBaseline: localBaseline,
			});
			debugLog(2, "[useBaselineActions][FLOW] updateBaseline started", { id: localBaseline?.id });

			const strArray = ["Basic Information", "Configurations", "Items", "Access Controls"];

			const baselineForSave = { ...localBaseline };

			// If step == -1, Save data only without prompt dialog
			// Example scenario: Save Item data, since the prompt will be taken care of in the BaselineItemConfig component
			const strMsg =
				step === -1 ? "" : `The Baseline ${strArray[activeStep]} was updated successfully!`;

			if (baselineForSave.number_of_period !== selectedBaseline.number_of_period) {
				baselineForSave.BL_CFG_TYPES = lookups.BL_CFG_TYPES;
			}
			debugLog(4, "[useBaselineActions][PROCESS] baselineForSave object", {
				baselineForSave: baselineForSave,
			});

			trackPromise(
				dispatch(
					updateBaseline({
						id: baselineForSave.id,
						data: baselineForSave,
					})
				)
					.unwrap()
					.then((response) => {
						debugLog(3, "[useBaselineActions][STATE] baseline updated", {
							response: response,
							step: step,
						});
					})
					.then(() => {
						// Save data only without prompt dialog
						if (strMsg !== "") {
							showDialog(
								{
									title: "Successful",
									message: strMsg,
								},
								() => {
									// get updated selectedBaseline
									fetchBaselineByIdOrUuid && fetchBaselineByIdOrUuid(baselineForSave.id);
									clearDataError();
								}
							);
						}

						let obj = {};
						if (activeStep === 0) {
							Object.keys(baselineForSave).forEach((key) => {
								var val = baselineForSave[key];
								if (typeof val !== "object") {
									obj[key] = val;
								}
							});
						} else if (activeStep === 1) {
							let arrayTemp = [];
							baselineForSave.baseline_configs.map((item) => {
								if (item.update_flag && item.update_flag !== 0) {
									arrayTemp.push({
										...item,
										photo: item.photo ? "placeholder" : "n/a",
									});
								}
								return false;
							});
							obj = {
								id: baselineForSave.id,
								configs: arrayTemp,
							};
						} else if (activeStep === 2) {
							let arrayTemp = [];
							baselineForSave.baseline_items.map((item) => {
								if (item.update_flag && item.update_flag !== 0) {
									arrayTemp.push({
										...item,
										photo: item.photo ? "placeholder" : "n/a",
									});
								}
								return false;
							});
							obj = {
								id: baselineForSave.id,
								items: arrayTemp,
							};
						} else if (activeStep === 3) {
							obj = {
								id: baselineForSave.id,
								items: baselineForSave.baseline_accesses,
							};
						}

						debugLog(4, "[useBaselineActions][PROCESS] updateBaselineAction event object", {
							eventObj: obj,
						});

						trackEvent({
							event: `updateBaseline-${activeStep + 1}`,
							props: { to: obj },
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineActions][FLOW] updateBaseline failed", {
							id: baselineForSave?.id,
							error: error,
						});
						console.error("updateBaseline failed", error);
						// Update is also a mutation, so keep the shared contract but
						// avoid retry; a partial save may already have been committed.
						withError(error, null, {
							fallbackMessage: "Baseline update failed.",
						});
					})
			);
		},
		[
			dispatch,
			localBaseline,
			activeStep,
			selectedBaseline,
			lookups,
			fetchBaselineByIdOrUuid,
			clearDataError,
			showDialog,
			trackEvent,
			withError,
		]
	);

	// DELETE - purge
	const deleteBaselineAction = useCallback(
		(id) => {
			debugLog(3, "[useBaselineActions][ACTION] deleteBaselineAction", { id: id });
			debugLog(2, "[useBaselineActions][FLOW] deleteBaseline started", { id: id });

			trackPromise(
				dispatch(deleteBaseline({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then(() => {
						debugLog(3, "[useBaselineActions][STATE] baseline deleted", { id: id });
						showDialog(
							{
								title: "Successful",
								message: ["The Baseline was purged successfully."],
							},
							() => handleBackToList && handleBackToList()
						);

						trackEvent({ event: `Baseline (#${id}) was purged`, props: null });
					})
					.catch((error) => {
						errorLog(2, "[useBaselineActions][FLOW] deleteBaseline failed", {
							id: id,
							error: error,
						});
						console.error("deleteBaseline failed", error);
						// Delete is a mutation, so keep the shared contract but avoid
						// retry; a purge may already have been committed server-side.
						withError(error, null, {
							fallbackMessage: "Baseline delete failed.",
						});
					})
			);
		},
		[dispatch, showDialog, handleBackToList, trackEvent, withError]
	);

	// COPY
	const copyBaselineAction = useCallback(
		(id) => {
			debugLog(3, "[useBaselineActions][ACTION] copyBaselineAction", { id: id });
			debugLog(2, "[useBaselineActions][FLOW] copyBaseline started", { id: id });

			trackPromise(
				dispatch(copyBaseline({ data: { id, updatedBy: Session().currUser.id } }))
					.unwrap()
					.then((response) => {
						const copiedId = response?.newId ?? response?.id ?? response?.baseline?.id;
						if (!copiedId) {
							throw new Error("Baseline copy response missing newId.");
						}
						debugLog(3, "[useBaselineActions][STATE] baseline copied", {
							id: id,
							newId: copiedId,
							response: response,
						});
						showDialog(
							{
								title: "Successful",
								message: ["The Baseline was duplicated successfully."],
							},
							() => handleBackToList && handleBackToList()
						);

						trackEvent({
							event: `Baseline was copied (#${id} to #${copiedId})`,
							props: null,
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineActions][FLOW] copyBaseline failed", {
							id: id,
							error: error,
						});
						console.error("copyBaseline failed", error);
						// Copy is also a mutation, so do not offer retry after a failed
						// response; the duplicate record may already exist.
						withError(error, null, {
							fallbackMessage: "Baseline copy failed.",
						});
					})
			);
		},
		[dispatch, showDialog, handleBackToList, trackEvent, withError]
	);

	// STATUS CHANGE - archive, restore and sfot delete
	const updateStatusAction = useCallback(
		(id, newStatus, showMsgBox = true) => {
			debugLog(3, "[useBaselineActions][ACTION] updateStatusAction", {
				id: id,
				newStatus: newStatus,
				showMsgBox: showMsgBox,
			});
			debugLog(2, "[useBaselineActions][FLOW] updateStatus started", {
				id: id,
				newStatus: newStatus,
			});

			let strAction = "archived";
			if (newStatus === 1) strAction = "re-activated";
			else if (newStatus === -1) strAction = "deleted";

			trackPromise(
				dispatch(
					updateStatus({
						id,
						status_id: newStatus,
						updatedBy: Session().currUser.id,
					})
				)
					.unwrap()
					.then(() => {
						debugLog(3, "[useBaselineActions][STATE] baseline status updated", {
							id: id,
							newStatus: newStatus,
							action: strAction,
						});

						if (showMsgBox) {
							showDialog(
								{
									title: "Successful",
									message: [
										`The Baseline "${localBaseline?.name}" was ${strAction} successfully!`,
									],
								},
								() => {
									if (newStatus === 1)
										fetchBaselineByIdOrUuid && fetchBaselineByIdOrUuid(id);
									else handleBackToList && handleBackToList();
								}
							);
						} else {
							if (newStatus === 1) fetchBaselineByIdOrUuid && fetchBaselineByIdOrUuid(id);
							else handleBackToList && handleBackToList();
						}

						trackEvent({ event: `Baseline (#${id}) was ${strAction}`, props: null });
					})
					.catch((error) => {
						errorLog(2, "[useBaselineActions][FLOW] updateStatus failed", {
							error: error.message,
						});
						// Status changes mutate server state, so keep the shared error
						// contract here without offering a retry action.
						withError(error, null, {
							fallbackMessage: "Baseline status update failed.",
						});
					})
			);
		},
		[
			dispatch,
			localBaseline,
			showDialog,
			fetchBaselineByIdOrUuid,
			handleBackToList,
			trackEvent,
			withError,
		]
	);

	// EXPORT
	const exportBaselineAction = useCallback(
		async ({ includePhotos = false } = {}) => {
			debugLog(3, "[useBaselineActions][ACTION] exportBaselineAction", {
				includePhotos: includePhotos,
				localBaseline: localBaseline,
			});
			debugLog(2, "[useBaselineActions][FLOW] exportBaseline started", {
				includePhotos: includePhotos,
			});

			let source = localBaseline ?? {};

			// Always export from a fresh backend read so JSON reflects full canonical data
			// (not potentially filtered/mutated local UI state). Photo payload remains opt-in.
			try {
				const idOrUuid = localBaseline?.id ?? localBaseline?.uuid;
				if (!idOrUuid) {
					throw new Error("Missing baseline identifier for export.");
				}
				source = await fetchBaselineForExport(idOrUuid, { includePhotos: includePhotos });
			} catch (err) {
				errorLog(2, "[useBaselineActions][FLOW] fetchBaselineForExport failed", {
					err: String(err),
				});
				showDialog({
					type: "error",
					title: "Error",
					message: [
						"Could not load baseline data for export.",
						"Please try again.",
					],
				});
				return;
			}

			// Keep only export-essential top-level objects and omit UI-derived `cfg_*` fields.
			const thisBaseline = Object.keys(source ?? {})
				.filter((key) => key.substring(0, 4) !== "cfg_" && key !== "scenarios")
				.reduce((obj, key) => {
					obj[key] = source[key];
					return obj;
				}, {}); // Important: initial accumulator
			debugLog(4, "[useBaselineActions][PROCESS] exportBaselineAction fetched baseline", {
				source: source,
				thisBaseline: thisBaseline,
			});
			// Nullify photo and thumbnail in baseline_item_configs
			if (Array.isArray(thisBaseline.baseline_items)) {
				thisBaseline.baseline_items = thisBaseline.baseline_items.map((item) => ({
					...item,
					photo: includePhotos ? item.photo ?? null : null,
					thumbnail: includePhotos ? item.thumbnail ?? null : null,
				}));
			}

			// Nullify photo and thumbnail in baseline_configs
			if (Array.isArray(thisBaseline.baseline_configs)) {
				thisBaseline.baseline_configs = thisBaseline.baseline_configs.map((config) => ({
					...config,
					photo: includePhotos ? config.photo ?? null : null,
					thumbnail: includePhotos ? config.thumbnail ?? null : null,
				}));
			}

			debugLog(4, "[useBaselineActions][PROCESS] exportBaselineAction cleaned baseline", {
				thisBaseline: thisBaseline,
				usedFreshPhotoFetch: includePhotos,
			});

			exportJSON(thisBaseline, `Baseline_${localBaseline.id}_${Session().loginUser.email}`);

			showDialog({
				title: "Successful",
				message: [`The data for Baseline (#${localBaseline.id}) was exported successfully.`],
			});

			trackEvent({
				event: `Baseline (#${localBaseline.id}) was exported`,
				props: null,
			});
		},
		[localBaseline, showDialog, trackEvent, fetchBaselineForExport]
	);

	// DUMP
	const dumpBaselineAction = useCallback(() => {
		debugLog(3, "[useBaselineActions][ACTION] dumpBaselineAction", {
			localBaseline: localBaseline,
		});
		debugLog(2, "[useBaselineActions][FLOW] dumpOneBaseline started", { id: localBaseline?.id });

		trackPromise(
			dispatch(dumpOneBaseline(localBaseline.id))
				.unwrap()
				.then((response) => {
					// Output in XLSX format
					let finalO = {};
					for (const key in response) {
						finalO[key] = response[key];
					}
					exportSpreadsheet(finalO, "xlsx");
					showDialog({
						title: "Successful",
						message: [`The data for Baseline (#${localBaseline.id}) was dumped successfully.`],
					});

					trackEvent({
						event: `Baseline data (#${localBaseline.id}) was dumped`,
						props: null,
					});
				})
				.catch((error) => {
					errorLog(2, "[useBaselineActions][FLOW] dumpOneBaseline failed", {
						id: localBaseline?.id,
						error: error,
					});
					console.error("dumpOneBaseline failed", error);
					showDialog({
						title: "Error",
						message: ["The data dump failed!", "Please try again."],
						type: "error",
					});
				})
		);
	}, [dispatch, localBaseline, showDialog, trackEvent]);

	// IMPORT
	const importBaselineAction = useCallback(
		(data) => {
			debugLog(3, "[useBaselineActions][ACTION] importBaselineAction", { data: data });
			debugLog(2, "[useBaselineActions][FLOW] importBaseline started", {});

			trackPromise(
				dispatch(importBaseline({ data: data }))
					.unwrap()
					.then((res) => {
						debugLog(3, "[useBaselineActions][STATE] baseline imported", {
							importedId: res.id,
							filename: data.filename,
						});

						showDialog(
							{
								title: "Successful",
								message: [`The Baseline (#${res.id}) was imported successfully.`],
							},
							() => handleBackToList && handleBackToList()
						);

						let obj = { id: res.id, source: data.filename };
						trackEvent({
							event: `importBaseline`,
							props: { to: obj },
						});
					})
					.catch((error) => {
						errorLog(2, "[useBaselineActions][FLOW] importBaseline failed", { error: error });
						console.error("importBaseline failed", error);
						// Keep import failures on the shared dialog contract, but do not
						// offer retry here because import is a non-idempotent mutation and
						// the server may already have created rows before the response failed.
						withError(error, null, {
							fallbackMessage: "Baseline import failed.",
						});
					})
			);
		},
		[dispatch, handleBackToList, showDialog, trackEvent, withError]
	);

	// Update Thumbnails
	const updateThumbnailsAction = useCallback(() => {
		debugLog(3, "[useBaselineActions][ACTION] updateThumbnailsAction", {
			localBaseline: localBaseline,
		});
		debugLog(2, "[useBaselineActions][FLOW] updateItemThumbnails started", {
			id: localBaseline?.id,
		});

		trackPromise(
			dispatch(updateItemThumbnails(localBaseline.id))
				.unwrap()
				.then((response) => {
					debugLog(3, "[useBaselineActions][STATE] baseline thumbnails updated", {
						response: response,
					});
					showDialog(
						{
							title: "Information",
							message: ["Click OKAY to reload thumbnails"],
						},
						() => {
							window.location.replace(
								`${process.env.REACT_APP_URI}/baseline/edit/?uuid=${localBaseline.uuid}`
							);
						}
					);

					trackEvent({
						event: `Item thumbnails for baseline (#${localBaseline.id}) was updated`,
						props: null,
					});
				})
				.catch((error) => {
					errorLog(2, "[useBaselineActions][FLOW] updateItemThumbnails failed", {
						id: localBaseline?.id,
						error: error,
					});
					console.error("updateItemThumbnails failed", error);
					showDialog({
						title: "Error",
						message: ["The item thumbnails update failed!", "Please try again."],
						type: "error",
					});
				})
		);
	}, [dispatch, localBaseline, showDialog, trackEvent]);

	return {
		createBaselineAction,
		updateBaselineAction,
		deleteBaselineAction,
		copyBaselineAction,
		updateStatusAction,
		exportBaselineAction,
		importBaselineAction,
		dumpBaselineAction,
		updateThumbnailsAction,
	};
}

// --- END useBaselineActions ---
